import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";
import { formatDate } from "../../../utils/helpers";
import { TableC } from "../../../CommonElements/TableC";
import { useGetHouseContractsQuery } from "../../../services/makkahGroups";
import { useLocalizedText } from "../../../utils/hooks/useLocalizedText";
import { Divider } from "antd";
import { Button, Stack } from "react-bootstrap";
import { HugeiconsIcon } from "@hugeicons/react";
import { CancelIcon } from "@hugeicons/core-free-icons";

export const DisplayDetailsModal = ({ details, open, onClose }) => {
  const { t, i18n } = useTranslation();
  const { data: contractsRes } = useGetHouseContractsQuery();
  const contracts = contractsRes?.data?.data || [];
  const { getLocalizedText, getLocalizedNumber } = useLocalizedText();

  // Define columns configuration with render functions
  const columns = useMemo(
    () => [
      {
        name: t("CONTRACT_NUMBER"),
        key: "contract_id",
        filterable: true,
        render: (value) => getLocalizedNumber(value) || "-",
      },
      {
        name: t("CONTRACT_START_DATE"),
        key: "contract_start_date",
        filterable: true,
        render: (value, rowData) => {
          const contract = contracts.find(
            (c) => String(c.id) === String(rowData?.contract_id)
          );
          return contract?.contract_start_date
            ? formatDate(
                contract.contract_start_date,
                i18n.language,
                "DD-MM-YYYY"
              )
            : "-";
        },
      },
      {
        name: t("CONTRACT_END_DATE"),
        key: "contract_end_date",
        filterable: true,
        render: (value, rowData) => {
          const contract = contracts.find(
            (c) => String(c.id) === String(rowData?.contract_id)
          );
          return contract?.contract_end_date
            ? formatDate(
                contract.contract_end_date,
                i18n.language,
                "DD-MM-YYYY"
              )
            : "-";
        },
      },
      {
        name: t("FLIGHT_DATE"),
        key: "flight_date",
        filterable: true,
        render: (value) =>
          value ? formatDate(value, i18n.language, "DD-MM-YYYY") : "-",
      },
      {
        name: t("FLIGHT_DAY"),
        key: "flight_arabic_day",
        filterable: true,
        render: (value, rowData) =>
          getLocalizedText(rowData?.day_name_ar, rowData?.day_name_en) || "-",
      },
      {
        name: t("FLIGHT_NUMBER"),
        key: "flight_number",
        filterable: true,
        render: (value) => getLocalizedNumber(value) || "-",
      },
      {
        name: t("FLIGHT_AIRLINE"),
        key: "flight_airline_info",
        filterable: true,
        render: (value) =>
          getLocalizedText(value?.name_ar, value?.name_en) || "-",
      },
      {
        name: t("DETAILS_CAPACITY"),
        key: "capacity",
        filterable: true,
        render: (value) => getLocalizedNumber(value) || "-",
      },
      {
        name: t("ENTRY_PORT"),
        key: "entry_port_info",
        filterable: true,
        render: (value) =>
          getLocalizedText(value?.name_ar, value?.name_en) || "-",
      },
      {
        name: t("ARRIVAL_ROUTE"),
        key: "coming_from_info",
        filterable: true,
        render: (value) =>
          getLocalizedText(value?.name_ar, value?.name_en) || "-",
      },
    ],
    [t, i18n.language]
  );

  return (
    <Modal isOpen={open} toggle={onClose} size="xl" centered>
      <ModalBody>
        <h5 className="text-center border-bottom border-1 pb-3 mb-3 fw-bold">
          {t("DETAILS_MODAL_TITLE")}
        </h5>
        <TableC
          data={details || []}
          columnsFilter={false}
          columns={columns}
          loading={false}
          emptyStateTitle={t("NO_DETAILS_FOUND")}
          emptyStateDescription={t("NO_DETAILS_DESCRIPTION")}
          headerKey="contract_number"
          minVisibleColumns={2}
          skeletonCount={3}
        />

        <Stack gap={2} className="pb-2 sticky-bottom">
          <Divider />
          <div>
            <Button onClick={onClose} className="btn-danger btn-xs">
              <HugeiconsIcon size={14} icon={CancelIcon} />
              {t("CLOSE")}
            </Button>
          </div>
        </Stack>
      </ModalBody>
    </Modal>
  );
};
